import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import { setUserContext, clearUserContext, addBreadcrumb } from '@/shared/lib/sentry';
import {
  setUserContext as setAxiomUserContext,
  clearUserContext as clearAxiomUserContext,
  trackEvent,
} from '@/shared/lib/axiom';
import type { UserPermissions } from '@/shared/types/permissions';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions?: UserPermissions;
}

interface ImpersonatedBy {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  impersonatedBy: ImpersonatedBy | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
  refreshCurrentUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';
const IMPERSONATED_BY_KEY = 'impersonatedBy';
const ORIGINAL_TOKEN_KEY = 'originalAccessToken';
const ORIGINAL_REFRESH_TOKEN_KEY = 'originalRefreshToken';
const ORIGINAL_USER_KEY = 'originalUser';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonatedBy, setImpersonatedBy] = useState<ImpersonatedBy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);
        const storedImpersonatedBy = localStorage.getItem(IMPERSONATED_BY_KEY);

        if (token && storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);

          // Load impersonation data if exists
          if (storedImpersonatedBy) {
            const impersonatedByData = JSON.parse(storedImpersonatedBy);
            setImpersonatedBy(impersonatedByData);
          }

          // Set Sentry user context
          setUserContext({
            id: userData.id,
            email: userData.email,
            name: userData.name,
            role: userData.role,
          });

          // Set Axiom user context
          setAxiomUserContext({
            id: userData.id,
            email: userData.email,
            name: userData.name,
            role: userData.role,
          });

          // Verify token is still valid and get fresh user data
          try {
            const response = await api.get('/auth/me', {
              headers: { Authorization: `Bearer ${token}` },
            });
            const freshUserData = response.data.data;
            setUser(freshUserData);
            // Update localStorage with fresh data (including permissions)
            localStorage.setItem(USER_KEY, JSON.stringify(freshUserData));
          } catch (error) {
            // Token invalid, try refresh
            await refreshToken();
          }
        }
      } catch (error) {
        console.error('Failed to load user:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  // Setup axios interceptor for auth
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retried, try refresh token
        // Also skip retry for auth endpoints to prevent infinite loops
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/login') &&
          !originalRequest.url?.includes('/auth/register') &&
          !originalRequest.url?.includes('/auth/refresh')
        ) {
          originalRequest._retry = true;

          try {
            await refreshToken();
            // Update the Authorization header with the new token
            const newToken = localStorage.getItem(TOKEN_KEY);
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return api(originalRequest);
          } catch (refreshError) {
            logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const refreshToken = async () => {
    const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshTokenValue) {
      throw new Error('No refresh token');
    }

    const response = await api.post('/auth/refresh', {
      refreshToken: refreshTokenValue,
    });

    const { tokens } = response.data.data;
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  };

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { user: userData, tokens } = response.data.data;

    // Save to localStorage
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));

    setUser(userData);

    // Set Sentry user context
    setUserContext({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
    });

    // Set Axiom user context
    setAxiomUserContext({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
    });

    // Track login event in Axiom
    trackEvent('user_login', {
      userId: userData.id,
      userEmail: userData.email,
      userRole: userData.role,
    });

    // Add breadcrumb
    addBreadcrumb({
      category: 'auth',
      message: 'User logged in',
      level: 'info',
    });

    // Redirect based on role
    if (userData.role === 'CLIENT') {
      navigate('/client/dashboard');
    } else if (userData.role === 'COLLABORATOR' || userData.role === 'GESTOR') {
      navigate('/people-core/dashboard');
    } else {
      navigate('/');
    }
  };

  const register = async (email: string, password: string, name: string) => {
    const response = await api.post('/auth/register', { email, password, name });
    const { user: userData, tokens } = response.data.data;

    // Save to localStorage
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));

    setUser(userData);

    // Set Sentry user context
    setUserContext({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
    });

    // Set Axiom user context
    setAxiomUserContext({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
    });

    // Track register event in Axiom
    trackEvent('user_register', {
      userId: userData.id,
      userEmail: userData.email,
      userRole: userData.role,
    });

    // Add breadcrumb
    addBreadcrumb({
      category: 'auth',
      message: 'User registered',
      level: 'info',
    });

    // Redirect based on role
    if (userData.role === 'CLIENT') {
      navigate('/client/dashboard');
    } else if (userData.role === 'COLLABORATOR' || userData.role === 'GESTOR') {
      navigate('/people-core/dashboard');
    } else {
      navigate('/');
    }
  };

  const logout = () => {
    // Track logout event before clearing user
    if (user) {
      trackEvent('user_logout', {
        userId: user.id,
      });
    }

    // Add breadcrumb before clearing user
    addBreadcrumb({
      category: 'auth',
      message: 'User logged out',
      level: 'info',
    });

    // Clear Sentry user context
    clearUserContext();

    // Clear Axiom user context
    clearAxiomUserContext();

    // Clear React Query cache (to prevent data leakage between users)
    queryClient.clear();

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(IMPERSONATED_BY_KEY);
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_REFRESH_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_USER_KEY);
    setUser(null);
    setImpersonatedBy(null);
    navigate('/login');
  };

  const impersonate = async (userId: string) => {
    try {
      // Save current admin session
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const currentUser = localStorage.getItem(USER_KEY);

      if (currentToken && currentRefreshToken && currentUser) {
        localStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);
        localStorage.setItem(ORIGINAL_REFRESH_TOKEN_KEY, currentRefreshToken);
        localStorage.setItem(ORIGINAL_USER_KEY, currentUser);
      }

      // Call impersonate API
      const response = await api.post(`/auth/impersonate/${userId}`);
      const { user: targetUser, tokens, impersonatedBy: impersonatedByData } = response.data.data;

      // Save impersonated session
      localStorage.setItem(TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(targetUser));
      localStorage.setItem(IMPERSONATED_BY_KEY, JSON.stringify(impersonatedByData));

      setUser(targetUser);
      setImpersonatedBy(impersonatedByData);

      // Clear React Query cache to prevent data leakage
      queryClient.clear();

      // Track impersonation event
      trackEvent('admin_impersonate', {
        adminId: impersonatedByData.id,
        targetUserId: targetUser.id,
      });

      // Navigate to client dashboard
      navigate('/client/dashboard');
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      throw error;
    }
  };

  const exitImpersonation = () => {
    // Restore original admin session
    const originalToken = localStorage.getItem(ORIGINAL_TOKEN_KEY);
    const originalRefreshToken = localStorage.getItem(ORIGINAL_REFRESH_TOKEN_KEY);
    const originalUser = localStorage.getItem(ORIGINAL_USER_KEY);

    if (originalToken && originalRefreshToken && originalUser) {
      localStorage.setItem(TOKEN_KEY, originalToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, originalRefreshToken);
      localStorage.setItem(USER_KEY, originalUser);

      const userData = JSON.parse(originalUser);
      setUser(userData);
      setImpersonatedBy(null);

      // Clear impersonation data
      localStorage.removeItem(IMPERSONATED_BY_KEY);
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      localStorage.removeItem(ORIGINAL_REFRESH_TOKEN_KEY);
      localStorage.removeItem(ORIGINAL_USER_KEY);

      // Clear React Query cache
      queryClient.clear();

      // Track exit impersonation event
      trackEvent('admin_exit_impersonate', {
        adminId: userData.id,
      });

      // Navigate based on user role
      // ADMIN -> /users
      // GESTOR, COLLABORATOR -> /clients
      if (userData.role === 'ADMIN') {
        navigate('/users');
      } else if (userData.role === 'GESTOR' || userData.role === 'COLLABORATOR') {
        navigate('/clients');
      } else {
        navigate('/');
      }
    }
  };

  const refreshCurrentUser = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        throw new Error('No access token');
      }

      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const userData = response.data.data;
      setUser(userData);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      // Update Sentry user context
      setUserContext({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
      });

      // Update Axiom user context
      setAxiomUserContext({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
      });
    } catch (error) {
      console.error('Failed to refresh current user:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        impersonatedBy,
        login,
        register,
        logout,
        impersonate,
        exitImpersonation,
        refreshCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
